import React, { useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { PropTypes } from 'prop-types';
import { Col, Row, message as toaster } from 'antd';
import { Form, Select } from 'formik-antd';
import { useDispatch } from 'react-redux';
import { Box, FormInput, CustomInput, Button } from 'atoms';
import { addUser } from 'store/pageSlice';
import { Debug } from './Debug';
import { ACL, EditModeBtn } from 'components/common';
// import { useQuery } from 'hooks';
import {
	driveModeOptions,
	fuelTypeOptions,
	sortedmakersOptions,
	vehicleTypeOptions,
} from 'utils/config';
import { CancelButton } from 'utils/commonStyled';

const FormSchema = Yup.object({
	vehicle_category: Yup.string().label('Vehicle Category').required(),
	maker: Yup.string()
		.matches(/^[a-zA-Z -&]*$/, 'Please enter valid model')
		.min(4)
		.max(20)
		.label('Maker')
		.required(),
	model: Yup.string()
		.matches(/^[a-zA-Z0-9 -.+]*$/, 'Please enter valid model')
		.max(100)
		.label('Model')
		.required()
		.trim(''),
	capacity: Yup.string()
		.matches(
			/^(0\.(?!0+$)\d+|[1-9]\d*(\.\d{1,2})?)$/,
			'Please enter a valid capacity with up to two decimal points and no spaces'
		)

		.trim()
		.min(0.1)
		.max(99999)
		.strict(true)
		.label('Capacity')
		.required('Capacity is required'),
	length: Yup.string()
		.matches(/^[0-9]*$/, 'This field can only contain numeric values')
		.label('Length')
		.required()
		.min(1)
		.max(2)
		.trim(''),
	fuel_type: Yup.string().label('Fuel Type').required(),
	drive_mode: Yup.string().label('Drive Mode').required(),
});
export default function AddVehicleForm({ isEditMode = false, formData }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	// const [query] = useQuery();
	const [editMode, setEditMode] = useState(false);
	const [isCreate, setIsCreate] = useState(false);

	const initialValues = useMemo(
		() => ({
			vehicle_category: formData?.info?.vehicle_category || undefined,
			maker: formData?.info?.maker || undefined,
			model: formData?.info?.model || undefined,
			capacity: formData?.info?.capacity || undefined,
			length: formData?.info?.length || undefined,
			fuel_type: formData?.info?.fuel_type || undefined,
			drive_mode: formData?.info?.drive_mode || undefined,
			...(isEditMode && { id: formData?.id }),
		}),
		[formData, isEditMode]
	);

	const handleSubmit = (values, { setErrors, setSubmitting }) => {
		const { maker, model, capacity, length, fuel_type, drive_mode, vehicle_category, ...rest } =
			values;

		const payload = {
			data: {
				...rest,
				is_active: true,
				info: {
					vehicle_category,
					maker,
					model,
					capacity,
					length: Number(length),
					fuel_type,
					drive_mode,
				},
			},
		};

		if (isEditMode) {
			payload.update_type = 'vehicle';
		} else {
			payload.create_type = 'vehicle';
		}

		dispatch(addUser({ data: payload, setErrors, isEditMode }))
			.then(() => {
				setSubmitting(false);
				toaster.success('Vehicle added successfully');
				navigate('/vehicles');
			})
			.catch(() => {
				setSubmitting(false);
			});
	};
	function handleEdit() {
		setEditMode(!editMode);
	}

	useEffect(() => {
		setIsCreate(location.pathname.split('/').pop() === 'add');
	});

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={FormSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<ACL action="update" module="client">
					{isEditMode && <EditModeBtn handleEdit={handleEdit} />}
				</ACL>
				<Form className={editMode === false && !isCreate ? 'disabled-div' : ''}>
					<Box padding="1.5rem 2rem" background="#fff">
						<Row gutter={16} justify="space-between">
							<Col lg={12}>
								<FormInput name="vehicle_category" label="Type of Category *">
									<Select
										showSearch
										name="vehicle_category"
										filterOption={(input, option) =>
											option.label
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
										disabled={isEditMode}
										placeholder="Select"
										options={vehicleTypeOptions}
									/>
								</FormInput>
							</Col>
							<Col lg={12}>
								<FormInput name="maker" label="Maker / Manufacturer *">
									<Select
										showSearch
										name="maker"
										filterOption={(input, option) =>
											option.label
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
										options={sortedmakersOptions}
										placeholder="Select"
										disabled={isEditMode}
									/>
								</FormInput>
							</Col>
						</Row>

						<Row gutter={16} justify="space-between">
							<Col lg={12}>
								<FormInput name="model" label="Model *">
									<CustomInput name="model" disabled={isEditMode} />
								</FormInput>
							</Col>
							<Col lg={12}>
								<FormInput name="capacity" label="Capacity (in tonne) *">
									<CustomInput name="capacity" />
								</FormInput>
							</Col>
						</Row>

						<Row gutter={16} justify="space-between">
							<Col lg={12}>
								<FormInput name="length" label="Length (in feet) *">
									<CustomInput name="length" />
								</FormInput>
							</Col>
							<Col lg={12}>
								<FormInput name="fuel_type" label="Fuel Type *">
									<Select
										showSearch
										name="fuel_type"
										filterOption={(input, option) =>
											option.label
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
										placeholder="Select"
										options={fuelTypeOptions}
									/>
								</FormInput>
							</Col>
						</Row>

						<Row gutter={16} justify="space-between">
							<Col lg={12}>
								<FormInput name="drive_mode" label="Drive Mode *">
									<Select
										showSearch
										name="drive_mode"
										filterOption={(input, option) =>
											option.label
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
										placeholder="Select"
										options={driveModeOptions}
									/>
								</FormInput>
							</Col>
						</Row>
					</Box>

					<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
						<CancelButton onClick={() => navigate('/vehicles')} type="button">
							Cancel
						</CancelButton>
						<Button borderRadius="4px" backgroundColor="#18405d" type="submit">
							{isEditMode ? 'Update' : 'Submit'}
						</Button>
					</Box>
					<Debug />
				</Form>
			</>
		</Formik>
	);
}

AddVehicleForm.propTypes = {
	isEditMode: PropTypes.bool,
	formData: PropTypes.object,
};
